/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import {
    Session as ISession,
    SessionLinearPlaybackTerminateTimeReasonEnum,
    SessionTransportEncodingTypeEnum,
    SessionTrickModeRestrictions,
} from 'client-code/session-manager';

export class Session {
    /**
     * Additional information that may be needed by the DRM system to enable access to the media.
     * @type {string}
     */
    drmSystemAuthorizationData?: string;

    /**
     * Interval in seconds used to calculate the delay between a SessionCreate response
     * and the first KeepAlive call.
     * @type {number}
     */
    firstKeepAliveSeconds?: number;

    /**
     * Interval in seconds between KeepAlive calls after the first KeepAlive call
     * that follows a SessionCreate response.
     * @type {number}
     */
    keepAliveIntervalSeconds: number;

    /**
     * The time (UTC) when the client must stop the streaming session.
     * This value is interpreted within the context of the original broadcast timeline.
     *  When the client is streaming the broadcast at the live point (live-streaming),
     * the stream must stop at the specified time. When the client is streaming from a buffer,
     * the stream must stop when it reaches the specified time in the timeline
     * of the original broadcast, irrespective of the client\s clock time.
     * @type {string}
     */
    linearPlaybackTerminateTime?: string;

    /**
     *
     * @type {SessionLinearPlaybackTerminateTimeReasonEnum}
     */
    linearPlaybackTerminateTimeReason?: SessionLinearPlaybackTerminateTimeReasonEnum;

    /**
     * A token representing a client\s authorization to play a specific
     * on-demand media offer or linear stream for some period of time.
     * A value may be supplied in this structure only in a Session Create response.
     * Any client supplying partnerAuthToken in a request must use the HTTP Authorization header.
     * The client will use the returned partnerAuthToken for all KeepAlive
     * and Session Delete calls for the one session.
     * @type {string}
     */
    partnerAuthToken?: string;

    /**
     * The playback policy associated with the session created.
     * Required only in the response to a session creation request.
     * @type {string}
     */
    playbackPolicy?: string;

    /**
     * A URL containing the path (possibly via redirects) to the streaming protocol\s
     * manifest file that can be used to play the requested media.
     * Required only in the response to a session creation request.
     * @type {string}
     */
    playlistUrl?: string;

    /**
     * A session manager-supplied ID that can be used to refer
     * to this session in subsequent requests.
     * @type {string}
     */
    sessionId: string;

    /**
     * A sub-object encapsulating the rules governing any trick-play
     * restrictions associated with the session.
     * @type {SessionTrickModeRestrictions}
     */
    trickModeRestrictions?: SessionTrickModeRestrictions;

    /**
     * The URL query string used on all media access URLs for this session.
     * Clients must add this string to each request URL of any media file
     * (manifest or transport stream) to the CDN.
     * The Session Manager may replace this value within a session in a KeepAlive response.
     * @type {string}
     */
    urlQueryString?: string;

    /**
     * The partner-supplied siteId for the VOD system supporting the specified client.
     * Only provided if the provider has multiple On Demand service sites for clients.
     * @type {string}
     */
    localityId?: string;

    /**
     * The TiVo-generated ID representing the partner supplying
     * entitlement services for the session media content.
     * @type {string}
     */
    partnerId: string;

    /**
     * The partner-supplied ID used by the partner entitlement system to
     * identify the customer device requesting playback.
     * @type {string}
     */
    partnerServicesBodyId?: string;

    /**
     * The partner-supplied ID used by the partner entitlement system to
     * identify the customer account requesting playback.
     * @type {string}
     */
    partnerServicesCustomerId: string;

    /**
     * A code indicating the current state of the session. Will be created
     * for new sessions in the Session Create response.
     * @type {string}
     */
    status: string;

    /**
     * TiVo\s device ID for the client device.
     * @type {string}
     */
    tsn: string;

    /**
     * The external WAN IP address of the client.
     * @type {string}
     */
    wanIpAddress?: string;

    /**
     *
     * @type {string}
     */
    drmType?: string;

    /**
     * Duration in seconds of the offer. Required when playing a past broadcast
     * offer that requires the player to stop at its end. A duration may only be
     * provided if partnerStationId and startTime are provided.
     * @type {number}
     */
    duration?: number;

    /**
     * The partner-supplied ID used by the partner session service to identify
     * the on-demand asset for which a session will be authorized.
     * @type {string}
     */
    partnerAssetId?: string;

    /**
     * The partner-supplied content ID of the stream for showing authorization level.
     * @type {string}
     */
    partnerContentId?: string;

    /**
     * The partner-supplied ID used by the partner session service to identify
     * the linear recording for which a session will be authorized.
     * @type {string}
     */
    partnerRecordingId?: string;

    /**
     * The root ID of the associated VOD offer, as assigned by the partner
     * VOD system, for which a session will be authorized.
     * @type {string}
     */
    partnerRootOfferId?: string;

    /**
     * The partner-supplied ID used by the partner session service to identify
     * the linear stream for which a session will be authorized.
     * For On Demand or linear recording sessions this will represent
     * the originating linear service.
     * @type {string}
     */
    partnerStationId?: string;

    /**
     * When playing a past broadcast offer, versus streaming from the live point,
     * the startTime indicates the specific past broadcast offer to start.
     * The manifest file accessed must allow client to start at the beginning
     * of this past broadcast offer. A startTime may only be provided if
     * partnerStationId is provided. If a value is provided in the response,
     * it indicates the actual showing startTime of the media in the playlist.
     * @type {string}
     */
    startTime?: string;

    /**
     * Transport stream encoding type for playback.
     * @type {SessionTransportEncodingTypeEnum}
     */
    transportEncodingType?: SessionTransportEncodingTypeEnum;

    constructor(params: ISession) {
        this.sessionId = params.sessionId;
        this.keepAliveIntervalSeconds = params.keepAliveIntervalSeconds;
        this.partnerId = params.partnerId;
        this.partnerServicesCustomerId = params.partnerServicesCustomerId;
        this.status = params.status;
        this.tsn = params.tsn;
        this.drmSystemAuthorizationData = params.drmSystemAuthorizationData;
        this.firstKeepAliveSeconds = params.firstKeepAliveSeconds;
        this.linearPlaybackTerminateTime = params.linearPlaybackTerminateTime;
        this.linearPlaybackTerminateTimeReason = params.linearPlaybackTerminateTimeReason;
        this.partnerAuthToken = params.partnerAuthToken;
        this.playbackPolicy = params.playbackPolicy;
        this.playlistUrl = params.playlistUrl;
        this.trickModeRestrictions = params.trickModeRestrictions;
        this.urlQueryString = params.urlQueryString;
        this.localityId = params.localityId;
        this.partnerServicesBodyId = params.partnerServicesBodyId;
        this.wanIpAddress = params.wanIpAddress;
        this.drmType = params.drmType;
        this.duration = params.duration;
        this.partnerAssetId = params.partnerAssetId;
        this.partnerContentId = params.partnerContentId;
        this.partnerRecordingId = params.partnerRecordingId;
        this.partnerRootOfferId = params.partnerRootOfferId;
        this.partnerStationId = params.partnerStationId;
        this.startTime = params.startTime;
        this.transportEncodingType = params.transportEncodingType;
    }
}
